import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Select, Input, Spin } from "antd";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getUserRole } from "../../hooks/getUserRoles";
import { useGate } from "../Subscription/entitlements";
import {
  getReport,
  getTemplate,
  generateReport,
  createTemplate,
  updateTemplate,
  finalizeReport,
  deleteReport,
  regenerateBlock,
  listStandardTemplates,
  getProjectName,
  canAnalyst,
  type ReportBlock,
  type BlockType,
  type SourceFamily,
  type Report,
  type StandardTemplate,
} from "./reporting.service";
import {
  getReportSources,
  findSourceLabel,
  groupLabel,
  type GroupedSources,
  type SourceOption,
} from "./sources.service";
import { BLOCK_META, ADD_BLOCK_ORDER, makeBlock } from "./blocks/blockMeta";
import BlockRenderer from "./blocks/BlockRenderer";
import PreviewModal from "./PreviewModal";
import ConvertToTemplateModal from "./ConvertToTemplateModal";
import "./Reporting.css";

type BuilderStatus = "draft" | "final" | "template";

// which source families feed which block type
const ALLOWED_FAMILIES: Record<BlockType, SourceFamily[]> = {
  text: [],
  metric: ["formula", "scorecard"],
  chart: ["dashboard"],
  table: ["doc_insights"],
  ai_narrative: ["formula", "scorecard", "dashboard", "doc_insights"],
};

// clicking a data source adds a block of this type
const FAMILY_TO_BLOCK: Record<SourceFamily, BlockType> = {
  dashboard: "chart",
  formula: "metric",
  scorecard: "metric",
  doc_insights: "table",
};

const GROUP_ORDER: SourceFamily[] = [
  "dashboard",
  "formula",
  "scorecard",
  "doc_insights",
];

const stripData = (blocks: ReportBlock[]): ReportBlock[] =>
  blocks.map(({ data, ...rest }, i) => ({ ...rest, order: i }));

const Icon = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ReportBuilder: React.FC = () => {
  const { t } = useTranslation("reporting");
  const { id: projectId = "", reportId } = useParams();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("templateId");
  const navigate = useNavigate();
  const role = getUserRole();
  const isAnalyst = canAnalyst(role);
  const gate = useGate();

  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState<GroupedSources | null>(null);
  const [blocks, setBlocks] = useState<ReportBlock[]>([]);
  const [name, setName] = useState("Untitled report");
  const [status, setStatus] = useState<BuilderStatus>("draft");
  const [report, setReport] = useState<Report | null>(null);
  const [savedReportId, setSavedReportId] = useState<string | null>(
    reportId ?? null,
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<SourceFamily>>(
    new Set<SourceFamily>(["dashboard"]),
  );
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [standardTemplates, setStandardTemplates] = useState<StandardTemplate[]>(
    [],
  );
  const [projectName, setProjectName] = useState<string | null>(null);

  const isTemplate = status === "template";
  const readOnly = !isAnalyst || status === "final";

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const srcPromise = getReportSources(projectId);
        const namePromise = getProjectName(projectId);
        if (reportId) {
          const r = await getReport(reportId);
          if (!alive) return;
          setReport(r);
          setBlocks(
            [...(r.content?.blocks ?? [])].sort((a, b) => a.order - b.order),
          );
          setName(r.name);
          setStatus(r.status);
          setSavedReportId(r.id);
        } else if (templateId) {
          const t = await getTemplate(templateId);
          if (!alive) return;
          setBlocks([...(t.blocks ?? [])].sort((a, b) => a.order - b.order));
          setName(t.name);
          setStatus("template");
        }
        const src = await srcPromise;
        if (alive) setSources(src);
        const pname = await namePromise;
        if (alive) setProjectName(pname);
        if (!reportId && !templateId) {
          const std = await listStandardTemplates().catch(() => []);
          if (alive) setStandardTemplates(std);
        }
      } catch (e: any) {
        if (alive)
          toast.error(e?.response?.data?.detail || t("reportBuilder.loadFailed"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId, reportId, templateId]);

  const selected = useMemo(
    () => blocks.find((b) => b.id === selectedId) ?? null,
    [blocks, selectedId],
  );

  const sourceLabelFor = useCallback(
    (b: ReportBlock): string | null =>
      findSourceLabel(sources, b.config.source?.family, b.config.source?.id),
    [sources],
  );

  // ── Block ops ───────────────────────────────────────────────────────────────
  const addBlock = (type: BlockType) => {
    if (readOnly) return;
    const block = makeBlock(type, blocks.length);
    setBlocks((prev) => [...prev, block]);
    setSelectedId(block.id);
  };

  const seedFromTemplate = (t: StandardTemplate) => {
    if (readOnly) return;
    const seeded = [...(t.blocks ?? [])]
      .sort((a, b) => a.order - b.order)
      .map((b, i) => ({ ...b, order: i }));
    setBlocks(seeded);
    setName(t.name);
    setSelectedId(null);
  };

  const addSourceBlock = (opt: SourceOption) => {
    if (readOnly) return;
    const type = FAMILY_TO_BLOCK[opt.family];
    const block = makeBlock(type, blocks.length);
    block.title = opt.label;
    block.config.source = { family: opt.family, id: opt.id };
    setBlocks((prev) => [...prev, block]);
    setSelectedId(block.id);
  };

  const patchBlock = (id: string, patch: Partial<ReportBlock>) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    );
  };
  const patchConfig = (id: string, cfg: Record<string, any>) => {
    setBlocks((prev) =>
      prev.map((b) =>
        b.id === id ? { ...b, config: { ...b.config, ...cfg } } : b,
      ),
    );
  };
  const removeBlock = (id: string) => {
    setBlocks((prev) =>
      prev.filter((b) => b.id !== id).map((b, i) => ({ ...b, order: i })),
    );
    setSelectedId(null);
  };

  // ── Save / generate ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (readOnly) return;
    if (!blocks.length) {
      toast.info(t("reportBuilder.addBlockFirst"));
      return;
    }
    // Generating AI narrative blocks consumes credits — pre-check the balance.
    const usesAi =
      !isTemplate && blocks.some((b) => (b.type as string) === "ai_narrative");
    if (usesAi && !gate.hasTokens()) {
      gate.requireTokens(0, () => {});
      return;
    }
    setSaving(true);
    try {
      if (isTemplate && templateId) {
        const tpl = await updateTemplate(templateId, {
          name,
          blocks: stripData(blocks),
        });
        setBlocks([...tpl.blocks].sort((a, b) => a.order - b.order));
        toast.success(t("reportBuilder.templateSaved"));
      } else {
        const prevId = savedReportId;
        const r = await generateReport(projectId, {
          name,
          structure: stripData(blocks),
        });
        setReport(r);
        setBlocks(
          [...(r.content?.blocks ?? [])].sort((a, b) => a.order - b.order),
        );
        setStatus(r.status);
        setSavedReportId(r.id);
        setSelectedId(null);
        // replace the previous draft snapshot so the list doesn't accumulate
        if (prevId && prevId !== r.id) {
          deleteReport(prevId).catch(() => undefined);
        }
        navigate(`/reporting/${projectId}/builder/${r.id}`, { replace: true });
        toast.success(t("reportBuilder.progressSaved"));
        if (usesAi) gate.refresh();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t("reportBuilder.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleConvert = async (tplName: string, description: string) => {
    setConverting(true);
    try {
      await createTemplate(projectId, {
        name: tplName,
        description,
        blocks: stripData(blocks),
      });
      toast.success(t("reportBuilder.convertedToTemplate"));
      setConvertOpen(false);
      navigate(`/reporting/${projectId}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t("reportBuilder.convertFailed"));
    } finally {
      setConverting(false);
    }
  };

  const handleFinalize = async () => {
    if (!savedReportId) return;
    setSaving(true);
    try {
      const r = await finalizeReport(savedReportId);
      setReport(r);
      setStatus(r.status);
      toast.success(t("reportBuilder.reportFinalized"));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t("reportBuilder.finalizeFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async (blockId: string) => {
    if (!savedReportId) {
      toast.info(t("reportBuilder.saveProgressFirst"));
      return;
    }
    if (!gate.hasTokens()) {
      gate.requireTokens(0, () => {});
      return;
    }
    try {
      const r = await regenerateBlock(savedReportId, blockId);
      setReport(r);
      setBlocks(
        [...(r.content?.blocks ?? [])].sort((a, b) => a.order - b.order),
      );
      toast.success(t("reportBuilder.blockRegenerated"));
      gate.refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t("reportBuilder.regenerateFailed"));
    }
  };

  // ── Source select options for the edit menu ─────────────────────────────────
  const sourceSelectOptions = (type: BlockType) => {
    if (!sources) return [];
    return ALLOWED_FAMILIES[type]
      .filter((fam) => (sources[fam] ?? []).length > 0)
      .flatMap((fam) => {
        const items = sources[fam];
        // For dashboard sources, group by dashboard name (sublabel)
        if (fam === "dashboard") {
          const grouped = new Map<string, typeof items>();
          items.forEach((o) => {
            const dashboardName = o.sublabel || "Other";
            if (!grouped.has(dashboardName)) {
              grouped.set(dashboardName, []);
            }
            grouped.get(dashboardName)!.push(o);
          });
          return Array.from(grouped.entries()).map(([dashboardName, cards]) => ({
            label: `— ${dashboardName}`,
            options: cards.map((o) => ({
              label: o.label,
              value: `${fam}:${o.id}`,
            })),
          }));
        }
        // Other families use the original group structure
        return [{
          label: groupLabel(fam),
          options: items.map((o) => ({
            label: o.label,
            value: `${fam}:${o.id}`,
          })),
        }];
      });
  };

  if (loading) {
    return (
      <div className="reporting-loading">
        <Spin />
      </div>
    );
  }

  const statusLabel = t(`common.status.${status}`);

  return (
    <div className="rb">
      {/* action bar */}
      <div className="rb-topbar">
        {!readOnly ? (
          <div className="rb-topbar__actions">
            <button className="rb-btn rb-btn--ghost" onClick={() => setPreviewOpen(true)}>
              {t("reportBuilder.preview")}
            </button>
            {!isTemplate && (
              <button
                className="rb-btn rb-btn--ghost"
                onClick={() => setConvertOpen(true)}
                disabled={!blocks.length}
              >
                {t("common.convertToTemplate")}
              </button>
            )}
            {!isTemplate && savedReportId && status === "draft" && (
              <button className="rb-btn rb-btn--ghost" onClick={handleFinalize} disabled={saving}>
                {t("reportBuilder.finalize")}
              </button>
            )}
            <button className="rb-btn rb-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? t("reportBuilder.saving") : t("reportBuilder.saveProgress")}
            </button>
          </div>
        ) : (
          <button className="rb-btn rb-btn--ghost" onClick={() => setPreviewOpen(true)}>
            {t("reportBuilder.preview")}
          </button>
        )}
      </div>

      <div className="rb-body">
        {/* canvas */}
        <div className="rb-canvas">
          <div className="rb-report">
            <div className="rb-report__head">
              <div className="rb-report__titlerow">
                <input
                  className="rb-report__title-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={readOnly}
                  placeholder="Untitled report"
                />
                <span className={`rt-badge rt-badge--${status}`}>
                  <span className="rt-dot" />
                  {statusLabel}
                </span>
              </div>
              <div className="rb-report__meta">
                {t("reportBuilder.projectLabel")} {projectName || projectId} &nbsp;·&nbsp; {t("reportBuilder.lastUpdatedLabel")}{" "}
                {dayjs(report?.updated_at ?? undefined).format("MMMM D, YYYY")}
              </div>
            </div>

            {blocks.length === 0 ? (
              <div className="rb-empty">
                <div className="rb-empty__glyph">▤</div>
                <h3>{t("reportBuilder.emptyTitle")}</h3>
                <p>{t("reportBuilder.emptyDescription")}</p>
                {!readOnly && standardTemplates.length > 0 && (
                  <div className="rb-empty__templates">
                    {standardTemplates.map((t) => (
                      <button
                        key={t.key}
                        className="rb-tplchip"
                        onClick={() => seedFromTemplate(t)}
                        title={t.description}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="rb-blocks">
                {[...blocks]
                  .sort((a, b) => a.order - b.order)
                  .map((b) => (
                    <BlockRenderer
                      key={b.id}
                      block={b}
                      selected={b.id === selectedId}
                      sourceLabel={sourceLabelFor(b)}
                      onSelect={() => !readOnly && setSelectedId(b.id)}
                    />
                  ))}
              </div>
            )}

            {!readOnly && (
              <button
                className="rb-addnew"
                onClick={() => addBlock("text")}
                title={t("reportBuilder.addTextBlockTitle")}
              >
                + {t("reportBuilder.addNewBlock")}
              </button>
            )}
          </div>
        </div>

        {/* right panel */}
        <aside className="rb-panel">
          {selected ? (
            <BlockEditMenu
              key={selected.id}
              block={selected}
              readOnly={readOnly}
              sourceOptions={sourceSelectOptions(selected.type)}
              onBack={() => setSelectedId(null)}
              onTitle={(t) => patchBlock(selected.id, { title: t })}
              onConfig={(c) => patchConfig(selected.id, c)}
              onDelete={() => removeBlock(selected.id)}
              onRegenerate={() => handleRegenerate(selected.id)}
              canRegenerate={!!savedReportId && !isTemplate}
            />
          ) : (
            <div className="rb-builder">
              <h2 className="rb-panel__title">{t("reportBuilder.panelTitle")}</h2>

              {!readOnly && (
                <>
                  <div className="rb-panel__label">{t("reportBuilder.addBlockLabel")}</div>
                  <div className="rb-addgrid">
                    {ADD_BLOCK_ORDER.map((t) => {
                      const m = BLOCK_META[t];
                      return (
                        <button
                          key={t}
                          className="rb-addbtn"
                          onClick={() => addBlock(t)}
                        >
                          <Icon d={m.icon} />
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <div className="rb-panel__label">{t("reportBuilder.dataSourcesLabel")}</div>
              <div className="rb-sources">
                {GROUP_ORDER.map((fam) => {
                  const items = sources?.[fam] ?? [];
                  const open = openGroups.has(fam);

                  // For dashboard sources, group by dashboard name (sublabel)
                  const groupedItems = fam === "dashboard"
                    ? (() => {
                        const grouped = new Map<string, typeof items>();
                        items.forEach((o) => {
                          const dashboardName = o.sublabel || "Other";
                          if (!grouped.has(dashboardName)) {
                            grouped.set(dashboardName, []);
                          }
                          grouped.get(dashboardName)!.push(o);
                        });
                        return Array.from(grouped.entries());
                      })()
                    : null;

                  return (
                    <div className="rb-srcgroup" key={fam}>
                      <button
                        className="rb-srcgroup__head"
                        onClick={() =>
                          setOpenGroups((prev) => {
                            const n = new Set(prev);
                            n.has(fam) ? n.delete(fam) : n.add(fam);
                            return n;
                          })
                        }
                      >
                        {groupLabel(fam)}
                        <span>{open ? "▾" : "▸"}</span>
                      </button>
                      {open && (
                        <div className="rb-srcgroup__items">
                          {items.length === 0 ? (
                            <div className="rb-srcgroup__empty">
                              {t("reportBuilder.noSourcesAvailable")}
                            </div>
                          ) : groupedItems ? (
                            // Render grouped dashboard items
                            groupedItems.map(([dashboardName, cards]) => (
                              <div key={dashboardName} style={{ marginBottom: "8px" }}>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", padding: "6px 10px", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                                  — {dashboardName}
                                </div>
                                {cards.map((o) => (
                                  <button
                                    key={`${fam}:${o.id}`}
                                    className="rb-srcitem"
                                    onClick={() => addSourceBlock(o)}
                                    disabled={readOnly}
                                    title={o.label}
                                    style={{ paddingLeft: "28px" }}
                                  >
                                    <Icon d={BLOCK_META[FAMILY_TO_BLOCK[fam]].icon} />
                                    <span>{o.label}</span>
                                  </button>
                                ))}
                              </div>
                            ))
                          ) : (
                            // Render ungrouped items for other families
                            items.map((o) => (
                              <button
                                key={`${fam}:${o.id}`}
                                className="rb-srcitem"
                                onClick={() => addSourceBlock(o)}
                                disabled={readOnly}
                                title={o.sublabel}
                              >
                                <Icon d={BLOCK_META[FAMILY_TO_BLOCK[fam]].icon} />
                                <span>{o.label}</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </aside>
      </div>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={name}
        blocks={blocks}
        sourceLabelFor={sourceLabelFor}
      />
      <ConvertToTemplateModal
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        defaultName={name}
        saving={converting}
        onConvert={handleConvert}
      />
    </div>
  );
};

// ── Right-panel block editor ────────────────────────────────────────────────

interface EditMenuProps {
  block: ReportBlock;
  readOnly: boolean;
  sourceOptions: { label: string; options: { label: string; value: string }[] }[];
  onBack: () => void;
  onTitle: (t: string) => void;
  onConfig: (c: Record<string, any>) => void;
  onDelete: () => void;
  onRegenerate: () => void;
  canRegenerate: boolean;
}

const TONE_OPTIONS = [
  "Donor / external",
  "Board / leadership",
  "Internal team",
  "Technical",
];
const LENGTH_OPTIONS = ["Short", "Medium", "Long"];

const BlockEditMenu: React.FC<EditMenuProps> = ({
  block,
  readOnly,
  sourceOptions,
  onBack,
  onTitle,
  onConfig,
  onDelete,
  onRegenerate,
  canRegenerate,
}) => {
  const { t } = useTranslation("reporting");
  const meta = BLOCK_META[block.type];
  const srcValue = block.config.source?.id
    ? `${block.config.source.family}:${block.config.source.id}`
    : undefined;

  return (
    <div className="rb-edit">
      <button className="rb-edit__back" onClick={onBack}>
        ← {t("reportBuilder.backToBuilder")}
      </button>
      <div className="rb-edit__type" style={{ color: meta.accent }}>
        <Icon d={meta.icon} /> {meta.label} {t("reportBuilder.blockSuffix")}
      </div>

      <div className="rb-field">
        <label>{t("reportBuilder.blockHeadingLabel")}</label>
        <Input
          value={block.title}
          onChange={(e) => onTitle(e.target.value)}
          disabled={readOnly}
          placeholder={meta.defaultTitle}
        />
      </div>

      {block.type === "text" ? (
        <div className="rb-field">
          <label>{t("reportBuilder.textFieldLabel")}</label>
          <Input.TextArea
            value={block.config.body ?? ""}
            onChange={(e) => onConfig({ body: e.target.value })}
            disabled={readOnly}
            rows={5}
            placeholder={t("reportBuilder.textPlaceholder")}
          />
        </div>
      ) : block.type === "ai_narrative" ? (
        <>
          <div className="rb-field">
            <label>{t("reportBuilder.toneLabel")}</label>
            <Select
              value={block.config.tone ?? TONE_OPTIONS[0]}
              onChange={(v) => onConfig({ tone: v })}
              disabled={readOnly}
              options={TONE_OPTIONS.map((tone) => ({ label: tone, value: tone }))}
              style={{ width: "100%" }}
            />
          </div>
          <div className="rb-field">
            <label>{t("reportBuilder.lengthLabel")}</label>
            <Select
              value={block.config.length ?? "Medium"}
              onChange={(v) => onConfig({ length: v })}
              disabled={readOnly}
              options={LENGTH_OPTIONS.map((len) => ({ label: len, value: len }))}
              style={{ width: "100%" }}
            />
          </div>
          <div className="rb-field">
            <label>{t("reportBuilder.noteLabel")}</label>
            <Input.TextArea
              value={block.config.note ?? ""}
              onChange={(e) => onConfig({ note: e.target.value })}
              disabled={readOnly}
              rows={3}
              placeholder={t("reportBuilder.notePlaceholder")}
            />
          </div>
          <p className="rb-edit__hint">
            {t("reportBuilder.aiNarrativeHint")}
          </p>
        </>
      ) : (
        <>
          <div className="rb-field">
            <label>{t("reportBuilder.sourceLabel")}</label>
            <Select
              value={srcValue}
              onChange={(v: string) => {
                const [family, id] = v.split(/:(.+)/);
                onConfig({ source: { family, id } });
              }}
              disabled={readOnly}
              placeholder={t("reportBuilder.selectSourcePlaceholder")}
              options={sourceOptions}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
            />
          </div>
          {block.type === "chart" && (
            <div className="rb-field">
              <label>{t("reportBuilder.chartTypeLabel")}</label>
              <Select
                value={block.config.chart_type ?? "bar"}
                onChange={(v) => onConfig({ chart_type: v })}
                disabled={readOnly}
                options={["bar", "line", "pie", "donut"].map((chartType) => ({
                  label: t(`reportBuilder.chartType.${chartType}`),
                  value: chartType,
                }))}
                style={{ width: "100%" }}
              />
            </div>
          )}
          <p className="rb-edit__hint">{meta.helper}</p>
        </>
      )}

      {!readOnly && (
        <div className="rb-edit__footer">
          {canRegenerate && block.type !== "text" && (
            <button className="rb-btn rb-btn--ghost" onClick={onRegenerate}>
              {t("reportBuilder.regenerate")}
            </button>
          )}
          <button className="rb-btn rb-btn--danger-ghost" onClick={onDelete}>
            {t("reportBuilder.deleteBlock")}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportBuilder;
